import { useCallback, useEffect, useRef, useState } from 'react';
import {
	Image,
	Keyboard,
	Platform,
	StyleSheet,
	Text,
	useWindowDimensions,
	View,
	type LayoutChangeEvent,
} from 'react-native';
import { HelperText, RadioButton, TextInput } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import { CrossfadeImage, type CrossfadeImageSource } from 'src/presentation/components/molecules/CrossFadeImage';
import MailResponseDialog from 'src/presentation/dialogs/MailResponseDialog';
import ResetPasswordDialog from 'src/presentation/dialogs/ResetPasswordDialog';
import VerifyDialog from 'src/presentation/dialogs/VerifyDialog';
import FieldError from 'src/presentation/components/atoms/FieldError';
import BackLink from 'src/presentation/components/molecules/BackLink';
import EmailField, { type EmailFieldHandle } from 'src/presentation/components/molecules/EmailField';
import PasswordField, { type PasswordFieldHandle } from 'src/presentation/components/molecules/PasswordField';
import LoadingOverlay from 'src/presentation/components/molecules/LoadingOverlay';
import { useLoginStore } from 'src/domain/stores/useLoginStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import BulletPoints from 'src/presentation/components/organisms/BulletPoints';
import type BulletPointModel from 'src/domain/models/BulletPointModel';
import ThermenFansLogo from 'assets/images/themenfans-logo.png';
import type { LoginScreenParams } from 'src/presentation/navigation/types/NavigationTypes';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import LoginFlowType from 'src/domain/models/LoginFlowType';
import LoginStepType from 'src/domain/models/LoginStepType';
import LoginPanelType from 'src/domain/models/LoginPanelType';
import loginBussloo from 'assets/images/login/Algemeen_beeld_Thermen_Bussloo.jpg';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import useWebAutoFocus from 'src/presentation/hooks/useWebAutoFocus';
import { readAuthenticateAccessToken } from 'src/core/models/BridgeEvents';
import Translations from 'src/core/utils/Translations';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

type LoginStepCopy = {
	header: string;
	description?: string;
};

const HORIZONTAL_MODAL_PADDING = Dimens.padding18;
const PANEL_VERTICAL_PADDING = Dimens.padding40;

const panelForStep = (step: LoginStepType, flow: LoginFlowType): LoginPanelType => {
	switch (step) {
		case LoginStepType.Email:
			return LoginPanelType.Email;
		case LoginStepType.Middle:
			return LoginPanelType.LoginChoice;
		case LoginStepType.Last:
			return flow === LoginFlowType.Register ? LoginPanelType.RegisterForm : LoginPanelType.Password;
	}
};

const LoginScreen = () => {
	const route = useRoute<RouteProp<{ params: LoginScreenParams | undefined }>>();
	const { width: windowWidth, height: windowHeight } = useWindowDimensions();
	const insets = useSafeAreaInsets();
	const [screen, setScreen] = useState({ width: Dimens.size0, height: Dimens.size0 });
	const [keyboardOverlap, setKeyboardOverlap] = useState(Dimens.size0);

	const {
		email,
		password,
		gender,
		firstName,
		infix,
		lastName,
		newPassword,
		repeatPassword,
		step,
		flow,
		isLoading,
		error,
		isResetPasswordProcessing,
		resetPasswordSuccessful,
		isResendVerificationProcessing,
		isResetPasswordDialogVisible,
		isRegistrationDoneDialogVisible,
		isMailSentDialogVisible,
		isVerificationDialogVisible,
		init,
		postCheckEmail,
		postLogin,
		postRegisterAccount,
		getVerifyMagicLink,
		getMeWithAccessToken,
		postMagicLink,
		postResendVerification,
		postForgotPassword,
		goToStep,
		setEmail,
		setPassword,
		setGender,
		setFirstName,
		setInfix,
		setLastName,
		setNewPassword,
		setRepeatPassword,
		clearError,
		setResetPasswordSuccessful,
		setResetPasswordDialogVisible,
		setRegistrationDoneDialogVisible,
		setMailSentDialogVisible,
		setVerificationDialogVisible,
	} = useLoginStore();
	const { captureEvent } = useServicesStore();

	const isWeb = Platform.OS === Constants.platformWeb;
	const isIos = Platform.OS === Constants.platformIos;
	const isRegisterForm = flow === LoginFlowType.Register && step === LoginStepType.Last;
	const showsLogo = !isRegisterForm;
	const showsTagline = isWeb && showsLogo && step !== LoginStepType.Email;
	const stepWidth = (screen.width || windowWidth) - HORIZONTAL_MODAL_PADDING * 2;
	// iOS reports a keyboard height that already covers the home indicator, Android excludes the navigation bar.
	const sheetSafeBottom = isIos && keyboardOverlap > Dimens.size0 ? Dimens.size0 : insets.bottom;
	const sheetChrome = sheetSafeBottom + keyboardOverlap;

	const maxStepHeight = Math.max(
		Dimens.size0,
		(screen.height || windowHeight) * (isRegisterForm ? Dimens.ratio95 : Dimens.ratio85) - sheetChrome,
	);

	const steps: LoginStepCopy[] = [
		{
			header: Translations.loginStep1Header,
			description: Translations.loginStep1Description,
		},
		{
			header: Translations.loginStep2Header,
		},
		{
			header: Translations.loginStep3Header,
		},
	];

	const bulletPoints: BulletPointModel[] = [
		{
			text: Translations.exclusiveAccessToPersonalAdvantages,
		},
		{
			text: Translations.easyWayToManageYourReservations,
		},
		{
			text: Translations.enjoyRelaxationAtHome,
		},
	];

	const lastCheckedMagicLink = useRef<string | null>(null);

	const isLoginFlow = flow === LoginFlowType.Login;
	const emailFieldRef = useWebAutoFocus<EmailFieldHandle>(step === LoginStepType.Email);
	const passwordLoginButtonRef = useWebAutoFocus<View>(isLoginFlow && step === LoginStepType.Middle);
	const passwordFieldRef = useWebAutoFocus<PasswordFieldHandle>(isLoginFlow && step === LoginStepType.Last);

	const [panelHeights, setPanelHeights] = useState<Partial<Record<LoginPanelType, number>>>({});

	const pagerOffset = useSharedValue(Dimens.size0);
	const sharedHeight = useSharedValue(Dimens.size400);
	const stepWidthRef = useRef(stepWidth);

	const animatedStyles = useAnimatedStyle(() => ({
		height: sharedHeight.value,
	}));

	const pagerStyle = useAnimatedStyle(() => ({
		transform: [{ translateX: -pagerOffset.value }],
	}));

	useEffect(() => {
		const onShow = Keyboard.addListener(Constants.keyboardEventDidShow, (event) =>
			setKeyboardOverlap(event.endCoordinates.height),
		);
		const onHide = Keyboard.addListener(Constants.keyboardEventDidHide, () => setKeyboardOverlap(Dimens.size0));

		return () => {
			onShow.remove();
			onHide.remove();
		};
	}, []);

	useAppStateScreenListener({ onInit: () => init() });

	const slideTo = useCallback(
		(index: LoginStepType = LoginStepType.Email, animated: boolean = true) => {
			Keyboard.dismiss();
			goToStep(index);

			const offset = stepWidthRef.current * index;
			pagerOffset.value = animated ? withTiming(offset) : offset;
		},
		[goToStep, pagerOffset],
	);

	useEffect(() => {
		if (stepWidthRef.current === stepWidth) {
			return;
		}

		stepWidthRef.current = stepWidth;
		pagerOffset.value = stepWidth * step;
	}, [stepWidth, step, pagerOffset]);

	useEffect(() => {
		const measured = panelHeights[panelForStep(step, flow)];

		if (measured !== undefined) {
			sharedHeight.value = withTiming(Math.min(measured + PANEL_VERTICAL_PADDING * 2, maxStepHeight));
		}
	}, [step, flow, panelHeights, maxStepHeight, sharedHeight]);

	const measureScreen = (event: LayoutChangeEvent) => {
		const { width, height } = event.nativeEvent.layout;

		setScreen((current) => (current.width === width && current.height === height ? current : { width, height }));
	};

	const measurePanel = (panel: LoginPanelType) => (event: LayoutChangeEvent) => {
		const measured = Math.ceil(event.nativeEvent.layout.height);

		setPanelHeights((current) => (current[panel] === measured ? current : { ...current, [panel]: measured }));
	};

	const checkEmailValid = () =>
		postCheckEmail({
			onSuccess: () => slideTo(LoginStepType.Middle),
			onRegister: () => slideTo(LoginStepType.Last),
		});

	const checkPasswordValid = () => postLogin({ onRejected: () => slideTo(LoginStepType.Last, false) });

	const registerMember = () =>
		postRegisterAccount({
			onSuccess: () => slideTo(LoginStepType.Email),
			onError: () => slideTo(LoginStepType.Last),
		});

	const checkMagicLinkValid = useCallback(
		async (magiclink: string) =>
			await getVerifyMagicLink({
				magicLink: magiclink,
				onInvalid: () => slideTo(LoginStepType.Middle, false),
			}),
		[getVerifyMagicLink, slideTo],
	);

	const handleAccessTokenLogin = useCallback(
		async (event: Event) => {
			const accessToken = readAuthenticateAccessToken(event);

			if (!accessToken) {
				return;
			}

			await getMeWithAccessToken({ accessToken });
		},
		[getMeWithAccessToken],
	);

	useEffect(() => {
		if (route.params?.magiclink && route.params.magiclink !== lastCheckedMagicLink.current) {
			lastCheckedMagicLink.current = route.params.magiclink;
			checkMagicLinkValid(route.params.magiclink).catch((error: ApiRequestError) =>
				captureEvent({ message: 'checkMagicLinkValid failed', extra: { error } }),
			);
		}
	}, [route.params?.magiclink, checkMagicLinkValid, captureEvent]);

	useEffect(() => {
		if (Platform.OS !== Constants.platformWeb) {
			return undefined;
		}

		window.addEventListener(Constants.bridgeEventAuthenticate, handleAccessTokenLogin);

		return () => {
			window.removeEventListener(Constants.bridgeEventAuthenticate, handleAccessTokenLogin);
		};
	}, [handleAccessTokenLogin]);

	const images: Record<LoginStepType, CrossfadeImageSource> = {
		[LoginStepType.Email]: loginBussloo,
		[LoginStepType.Middle]: loginBussloo,
		[LoginStepType.Last]: loginBussloo,
	};

	const emailStepError = error?.errors?.['email']?.[0] ?? error?.message ?? null;

	const openResetPasswordDialog = () => {
		setResetPasswordSuccessful(null);
		setResetPasswordDialogVisible(true);
	};

	const renderEmailStep = (copy: LoginStepCopy, stepIndex: number) => {
		return (
			<Animated.ScrollView
				contentContainerStyle={styles.panelContent}
				key={stepIndex}
				showsVerticalScrollIndicator={false}
				style={[
					{
						width: stepWidth,
					},
					animatedStyles,
				]}
			>
				<View onLayout={measurePanel(LoginPanelType.Email)}>
					<Text style={styles.loginCarouselHeader}>{copy.header}</Text>
					<Text style={styles.loginCarouselDescription}>{copy.description}</Text>
					<EmailField
						hasError={emailStepError !== null}
						label={Translations.emailAddress}
						ref={emailFieldRef}
						value={email}
						onChangeText={(value) => setEmail(value)}
						onKeyPress={() => clearError()}
						onPressIn={() => clearError()}
						onSubmitEditing={checkEmailValid}
					/>
					<FieldError message={emailStepError} />
					<View style={styles.emailStepButtonsRow}>
						<PrimaryButton
							disabled={!email}
							extraStyle={styles.loginNextButton}
							label={Translations.continue}
							onPress={checkEmailValid}
						/>
					</View>
					<View style={styles.bulletPointsContainer}>
						<BulletPoints bulletPoints={bulletPoints} />
					</View>
				</View>
			</Animated.ScrollView>
		);
	};

	const renderLoginChoiceStep = (copy: LoginStepCopy, stepIndex: number) => {
		return (
			<Animated.ScrollView
				contentContainerStyle={styles.panelContent}
				key={stepIndex}
				showsVerticalScrollIndicator={false}
				style={[
					{
						width: stepWidth,
					},
					animatedStyles,
				]}
			>
				<View onLayout={measurePanel(LoginPanelType.LoginChoice)}>
					{error?.message ? (
						<HelperText style={styles.errorHelperText} type="error" visible={true}>
							{error.message}
						</HelperText>
					) : null}
					<Text style={styles.loginCarouselHeader}>{copy.header}</Text>
					<View style={styles.loginChoiceColumn}>
						<PrimaryButton
							label={Translations.loginWithPassword}
							ref={passwordLoginButtonRef}
							onPress={() => {
								slideTo(LoginStepType.Last);
							}}
						/>
						<PrimaryButton label={Translations.loginWithMagiclink} onPress={() => postMagicLink()} />
						<View style={styles.loginChoiceFooterRow}>
							<BackLink
								onPress={() => {
									slideTo(LoginStepType.Email);
								}}
							/>
							<Text style={styles.resetPasswordLink} onPress={openResetPasswordDialog}>
								{Translations.loginForgotPasswordLink}
							</Text>
						</View>
					</View>
				</View>
			</Animated.ScrollView>
		);
	};

	const renderPasswordStep = (copy: LoginStepCopy, stepIndex: number) => {
		return (
			<Animated.ScrollView
				contentContainerStyle={styles.panelContent}
				key={stepIndex}
				showsVerticalScrollIndicator={false}
				style={[
					{
						width: stepWidth,
					},
					animatedStyles,
				]}
			>
				<View onLayout={measurePanel(LoginPanelType.Password)}>
					<Text style={styles.loginCarouselHeader}>{copy.header}</Text>
					<Text style={[styles.loginCarouselDescription, styles.resetPasswordLink]} onPress={openResetPasswordDialog}>
						{Translations.loginForgotPasswordLink}
					</Text>
					<PasswordField
						hasError={!!error}
						label={Translations.password}
						ref={passwordFieldRef}
						value={password}
						onChangeText={(value) => setPassword(value)}
						onKeyPress={() => clearError()}
						onPressIn={() => clearError()}
						onSubmitEditing={checkPasswordValid}
					/>
					{error?.message ? (
						<HelperText style={styles.errorHelperText} type="error" visible={true}>
							{error.message}
						</HelperText>
					) : null}
					<View style={styles.stepButtonsRow}>
						<BackLink
							onPress={() => {
								slideTo(LoginStepType.Middle);
							}}
						/>
						<PrimaryButton extraStyle={styles.loginNextButton} label={Translations.doLogin} onPress={checkPasswordValid} />
					</View>
				</View>
			</Animated.ScrollView>
		);
	};

	const renderRegisterFormStep = (stepIndex: number) => {
		const errors = error?.errors;

		return (
			<Animated.ScrollView
				contentContainerStyle={styles.panelContent}
				key={stepIndex}
				showsVerticalScrollIndicator={false}
				style={[
					{
						width: stepWidth,
					},
					animatedStyles,
				]}
			>
				<View onLayout={measurePanel(LoginPanelType.RegisterForm)}>
					<Text style={styles.loginCarouselHeader}>{Translations.registerStep2Header}</Text>
					<Text style={styles.loginCarouselDescription}>{Translations.registerStep2Description}</Text>

					<RadioButton.Group value={gender} onValueChange={(value) => setGender(value)}>
						<View style={styles.genderRow}>
							<View style={styles.genderOption}>
								<RadioButton.Android color={Colors.green} value="female" />
								<Text style={styles.genderLabel} onPress={() => setGender(Constants.genderFemale)}>
									{Translations.madam}
								</Text>
							</View>
							<View style={styles.genderOption}>
								<RadioButton.Android color={Colors.green} value="male" />
								<Text style={styles.genderLabel} onPress={() => setGender(Constants.genderMale)}>
									{Translations.sir}
								</Text>
							</View>
							<View style={styles.genderOption}>
								<RadioButton.Android color={Colors.green} value="other" />
								<Text style={styles.genderLabel} onPress={() => setGender(Constants.genderOther)}>
									{Translations.genderPreferNotToSay}
								</Text>
							</View>
						</View>
					</RadioButton.Group>

					<FieldError message={errors?.['gender']} />

					<View style={styles.nameRow}>
						<View style={{ flex: Dimens.flex1 }}>
							<TextInput
								error={!!errors?.['name']}
								label={Translations.firstName}
								secureTextEntry={false}
								style={[
									styles.registerInput,
									{
										backgroundColor: Colors.gray100,
										zIndex: Dimens.zIndexOverlay,
									},
								]}
								value={firstName}
								onChangeText={(value) => setFirstName(value)}
							/>
							<FieldError message={errors?.['name']} />
						</View>
						<View style={{ flex: Dimens.flex1 }}>
							<TextInput
								error={!!errors?.['infix']}
								label={Translations.infixName}
								style={[
									styles.registerInput,
									{
										backgroundColor: Colors.gray100,
										zIndex: Dimens.zIndexOverlay,
									},
								]}
								value={infix}
								onChangeText={(value) => setInfix(value)}
							/>
							<FieldError message={errors?.['infix']} />
						</View>
					</View>

					<TextInput
						error={!!errors?.['last_name']}
						label={Translations.lastName}
						style={[
							styles.registerInput,
							{
								backgroundColor: Colors.gray100,
								zIndex: Dimens.zIndexOverlay,
							},
						]}
						value={lastName}
						onChangeText={(value) => setLastName(value)}
					/>
					<FieldError message={errors?.['last_name']} />

					<EmailField
						hasError={!!errors?.['email']}
						label={Translations.emailAddress}
						style={styles.registerInput}
						value={email}
						onChangeText={(value) => setEmail(value)}
					/>
					<FieldError message={errors?.['email']} />
					<PasswordField
						hasError={!!errors?.['password']}
						isNewPassword={true}
						label={Translations.passwordWithRules}
						style={styles.registerInput}
						value={newPassword}
						onChangeText={(value) => setNewPassword(value)}
					/>
					<FieldError message={errors?.['password']} />

					{newPassword !== '' ? (
						<>
							<PasswordField
								hasError={!!errors?.['password_confirmation']}
								isNewPassword={true}
								label={Translations.repeatPassword}
								style={styles.registerInput}
								value={repeatPassword}
								onChangeText={(value) => setRepeatPassword(value)}
							/>
							<FieldError message={errors?.['password_confirmation']} />
						</>
					) : null}

					{error?.message ? (
						<HelperText style={styles.errorHelperText} type="error" visible={true}>
							{error.message}
						</HelperText>
					) : null}
					<View style={styles.stepButtonsRow}>
						<BackLink
							onPress={() => {
								slideTo(LoginStepType.Email);
							}}
						/>
						<PrimaryButton extraStyle={styles.loginNextButton} label={Translations.doRegister} onPress={registerMember} />
					</View>
				</View>
			</Animated.ScrollView>
		);
	};

	return (
		<View style={styles.screen} onLayout={measureScreen}>
			<View style={styles.imageRegion}>
				<CrossfadeImage resizeMode="cover" source={images[step]} style={styles.backgroundImage} />
				<LinearGradient colors={[Colors.transparent, Colors.black66]} pointerEvents="none" style={styles.imageScrim} />
				{showsLogo ? (
					<View pointerEvents="none" style={[styles.imageOverlay, styles.imageOverlayLowered]}>
						<Image
							resizeMode="contain"
							source={ThermenFansLogo}
							style={[styles.overlayLogo, showsTagline ? styles.overlayLogoWithTagline : null]}
						/>
						{showsTagline ? <Text style={styles.overlayText}>{Translations.loginOverlayTagline}</Text> : null}
					</View>
				) : null}
			</View>

			<MailResponseDialog
				buttonText={Translations.close}
				text={Translations.checkYourEmailAndClickOnTheLinkToConfirmYourRegistration}
				title={Translations.youAreAlmostThere}
				visible={isRegistrationDoneDialogVisible}
				onPress={() => setRegistrationDoneDialogVisible(false)}
			/>
			<ResetPasswordDialog
				buttonText={Translations.resetpasswordCloseModal}
				hideResetButton={resetPasswordSuccessful !== null}
				isLoading={isResetPasswordProcessing}
				resetText={Translations.resetpasswordResetPassword}
				text={
					resetPasswordSuccessful
						? Translations.resetpasswordResetDescriptionSuccess
						: Translations.resetpasswordResetDescription
				}
				title={resetPasswordSuccessful ? Translations.resetpasswordResetConfirmed : Translations.resetpasswordResetConfirm}
				visible={isResetPasswordDialogVisible}
				onPress={() => setResetPasswordDialogVisible(false)}
				onPressReset={() => postForgotPassword()}
			/>
			<VerifyDialog
				buttonText={Translations.close}
				buttonTextVerify={Translations.resend}
				isLoading={isResendVerificationProcessing}
				text={Translations.yourAccountMustBeVerifiedBeforeYouCanLogInAnEmailHasBeenSentToYouWithAVerificationLink}
				title={Translations.anEmailIsOnItsWayToYou}
				visible={isVerificationDialogVisible}
				onPress={() => setVerificationDialogVisible(false)}
				onPressVerify={() => postResendVerification()}
			/>
			<MailResponseDialog
				buttonText={Translations.close}
				text={
					Translations.clickOnTheLinkInTheEmailToLogInImmediatelyDidntReceiveAnEmailItMayTakeAFewMinutesBeforeYouReceiveTheEmailAlsoJustToBeSureCheckYourJunkMail
				}
				title={Translations.anEmailIsOnItsWayToYou}
				visible={isMailSentDialogVisible}
				onPress={() => setMailSentDialogVisible(false)}
			/>

			<View style={styles.sheetBackdrop}>
				<View style={[styles.loginBottomSheet, { paddingBottom: sheetSafeBottom + keyboardOverlap }]}>
					{isLoading ? <LoadingOverlay /> : null}
					<View style={[styles.pager, { width: stepWidth }]}>
						<Animated.View style={[styles.pagerTrack, { width: stepWidth * steps.length }, pagerStyle]}>
							{steps.map((carouselStep, stepIndex) => (
								<View
									accessibilityElementsHidden={stepIndex !== step}
									importantForAccessibility={stepIndex === step ? 'auto' : 'no-hide-descendants'}
									key={stepIndex}
									pointerEvents={stepIndex === step ? 'auto' : 'none'}
									style={{ width: stepWidth }}
								>
									{stepIndex === LoginStepType.Email ? renderEmailStep(carouselStep, stepIndex) : null}

									{flow === LoginFlowType.Login && stepIndex === LoginStepType.Middle
										? renderLoginChoiceStep(carouselStep, stepIndex)
										: null}

									{flow === LoginFlowType.Login && stepIndex === LoginStepType.Last
										? renderPasswordStep(carouselStep, stepIndex)
										: null}

									{flow === LoginFlowType.Register && stepIndex === LoginStepType.Last
										? renderRegisterFormStep(stepIndex)
										: null}
								</View>
							))}
						</Animated.View>
					</View>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	screen: {
		flex: Dimens.flex1,
	},
	imageRegion: {
		flex: Dimens.flex1,
		backgroundColor: Colors.gray900,
	},
	backgroundImage: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
		right: Dimens.padding0,
		bottom: Dimens.padding0,
	},
	imageScrim: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
		right: Dimens.padding0,
		bottom: Dimens.padding0,
	},
	imageOverlay: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
		right: Dimens.padding0,
		bottom: Dimens.padding0,
		alignItems: 'center',
	},
	imageOverlayLowered: {
		justifyContent: 'flex-end',
		paddingBottom: Dimens.padding60,
	},
	overlayLogo: {
		width: Dimens.size170,
		height: Dimens.size40,
	},
	overlayLogoWithTagline: {
		marginBottom: Dimens.padding16,
	},
	overlayText: {
		fontSize: Dimens.fontSize28,
		fontFamily: Fonts.ptSerif,
		color: Colors.white,
		textAlign: 'center',
		textShadowColor: Colors.black99,
		textShadowOffset: { width: Dimens.size0, height: Dimens.size1 },
		textShadowRadius: Dimens.size4,
	},
	// The sheet colour has to carry through the padding the keyboard adds on iOS.
	sheetBackdrop: {
		backgroundColor: Colors.white,
	},
	pager: {
		overflow: 'hidden',
	},
	panelContent: {
		paddingVertical: PANEL_VERTICAL_PADDING,
	},
	pagerTrack: {
		flexDirection: 'row',
	},
	loginBottomSheet: {
		paddingHorizontal: HORIZONTAL_MODAL_PADDING,
		gap: Dimens.gap30,
		flexDirection: 'column',
		borderTopLeftRadius: Dimens.borderRadius4,
		borderTopRightRadius: Dimens.borderRadius4,
		marginTop: Dimens.paddingMinus5,
		backgroundColor: Colors.white,
		overflow: 'hidden',
	},
	loginCarouselHeader: {
		fontSize: Dimens.fontSize24,
		lineHeight: Dimens.lineHeight32,
		color: Colors.gray800,
		fontFamily: Fonts.ptSerif,
		marginBottom: Dimens.padding4,
		textAlign: 'center',
	},
	loginCarouselDescription: {
		...Platform.select({ web: { textWrapStyle: 'pretty' as const }, default: {} }),
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray600,
		textAlign: 'center',
		paddingBottom: Dimens.padding32,
		fontFamily: Fonts.barlow,
		paddingHorizontal: Dimens.padding0,
		alignSelf: 'center',
	},
	loginNextButton: {
		flex: Dimens.flex1,
	},
	bulletPointsContainer: {
		alignSelf: 'stretch',
	},
	emailStepButtonsRow: {
		flexDirection: 'row',
		justifyContent: 'center',
		marginTop: Dimens.padding16,
		paddingBottom: Dimens.padding24,
	},
	loginChoiceColumn: {
		flexDirection: 'column',
		gap: Dimens.gap16,
		paddingTop: Dimens.padding20,
	},
	loginChoiceFooterRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: Dimens.padding20,
	},
	errorHelperText: {
		marginBottom: Dimens.padding10,
	},
	resetPasswordLink: {
		textDecorationLine: 'underline',
		color: Colors.green,
	},
	stepButtonsRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: Dimens.gap16,
		marginTop: Dimens.padding16,
	},
	genderRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: Dimens.gap2,
	},
	registerInput: {
		marginBottom: Dimens.padding12,
	},
	genderOption: {
		flexDirection: 'row',
	},
	genderLabel: {
		lineHeight: Dimens.lineHeight38,
	},
	nameRow: {
		gap: Dimens.gap6,
		flexDirection: 'row',
	},
});

export default LoginScreen;
